import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ExternalLink, Sparkles } from "lucide-react";
import { api, resolveAsset, trackNavClick } from "../lib/api";

/**
 * Home page "Explore" tiles — auto-rotated from the header nav menu.
 *
 * Behaviour:
 *  - Pulls every enabled nav item from /api/nav-items, drops the home entry,
 *    and uses a date-seeded shuffle so every visitor sees the same 3 picks
 *    all day. New shuffle at UTC midnight.
 *  - Subtitle is auto-pulled from /api/page-content (built-in pages like
 *    /news, /retro, /stats) or /api/custom-pages (for /page/<slug> entries).
 *  - Image + tile colour come from the nav item itself (admin can set them at
 *    /admin/nav). When no image is uploaded the tile falls back to a solid
 *    colour block — same look as the manual page-tiles grid.
 */

const DEFAULT_TILE_COLOR = "#002d75";


// Deterministic mulberry32 PRNG — same seed always yields the same sequence.
function makeRng(seed) {
  let t = seed >>> 0;
  return function rng() {
    t = (t + 0x6d2b79f5) >>> 0;
    let r = t;
    r = Math.imul(r ^ (r >>> 15), r | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

// Hash a YYYY-MM-DD string into a 32-bit int for the PRNG seed.
function dateSeed() {
  const s = new Date().toISOString().slice(0, 10); // UTC-anchored
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return h;
}

// Fisher–Yates shuffle using the seeded RNG.
function seededShuffle(arr, rng) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// A few built-in routes were renamed but their nav `to` paths still
// point at the legacy URL. Map nav `to` paths to their page-content key
// so the Explore tile subtitle resolves correctly. Update this whenever
// a route gets re-pathed without a matching page-content rename.
const PAGE_CONTENT_ALIAS = {
  "blast-from-the-past": "retro",
};

// Pull the page_content key out of a nav `to` path.
// e.g. "/news" → "news", "/page/lasthistory" → null (handled separately).
function pageContentKeyFor(to) {
  if (!to || typeof to !== "string") return null;
  if (!to.startsWith("/")) return null;
  const path = to.split("?")[0].split("#")[0];
  if (path.startsWith("/page/")) return null;
  const key = path.replace(/^\/+/, "").replace(/\/+$/, "").toLowerCase();
  if (!key) return null;
  return PAGE_CONTENT_ALIAS[key] || key;
}

// Pull the custom page slug out of a /page/<slug> path.
function customPageSlugFor(to) {
  if (!to || typeof to !== "string") return null;
  const m = to.match(/^\/page\/([a-z0-9-]+)/i);
  return m ? m[1] : null;
}

function isHomePath(to) {
  if (!to) return true;
  const trimmed = to.trim().toLowerCase();
  return trimmed === "/" || trimmed === "/home" || trimmed === "";
}

function isExternal(to) {
  return /^https?:\/\//i.test(to || "");
}


export default function HomeRotatingTiles() {
  const [navItems, setNavItems] = useState(null);
  const [pageContent, setPageContent] = useState({});
  const [customPages, setCustomPages] = useState([]);
  const [pageTiles, setPageTiles] = useState([]);
  const [topNavId, setTopNavId] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/nav-items").then((r) => (Array.isArray(r.data) ? r.data : [])).catch(() => []),
      api.get("/page-content").then((r) => r.data || {}).catch(() => ({})),
      api.get("/custom-pages").then((r) => (Array.isArray(r.data) ? r.data : [])).catch(() => []),
      api.get("/nav-items/top").then((r) => r.data?.nav_item_id || null).catch(() => null),
      // Any tile the admin has ticked "Include in home Explore" on the
      // "Tiles on any page" admin screen — merged into the random pool.
      api.get("/page-tiles/explore-pool").then((r) => (Array.isArray(r.data) ? r.data : [])).catch(() => []),
    ]).then(([nav, pc, cp, top, tiles]) => {
      if (!alive) return;
      setNavItems(nav);
      setPageContent(pc);
      setCustomPages(cp);
      setTopNavId(top);
      setPageTiles(tiles);
    });
    return () => { alive = false; };
  }, []);

  const customPageBySlug = useMemo(() => {
    const map = {};
    (customPages || []).forEach((p) => { map[p.slug] = p; });
    return map;
  }, [customPages]);

  const picks = useMemo(() => {
    if (!navItems) return null;
    const eligibleNav = navItems.filter(
      (n) => n && n.enabled !== false && n.explore_pickable !== false && !isHomePath(n.to),
    );
    // Universal page tiles the admin has ticked "Include in home Explore"
    // — projected into the same shape so the random shuffle can mix them
    // with the header nav buttons.
    const eligibleTiles = (pageTiles || []).map((t) => {
      // Resolve where this tile points so the home tile click takes you
      // somewhere useful. Mirrors the link logic in PageTilesGrid.
      let to = "/";
      let external = false;
      if (t.link_type === "external" && t.external_url) {
        to = t.external_url;
        external = true;
      } else if (t.link_type === "internal" && t.internal_path) {
        to = t.internal_path;
      } else if (t.link_type === "custom_page" && t.custom_page_slug) {
        to = `/page/${t.custom_page_slug}`;
      }
      return {
        id: `tile-${t.id}`,
        label: t.title || "",
        to,
        target: external ? "blank" : "self",
        enabled: true,
        builtin: false,
        position: 9999,
        image_url: t.image_url || "",
        tile_color: t.tile_color || "",
        kind: "tile",
        _explore_tile_subtitle: t.subtitle || "",
        _explore_force_external: external,
      };
    });
    const eligible = [...eligibleNav, ...eligibleTiles];
    if (eligible.length === 0) return [];
    // Lead with yesterday's most-clicked nav item (if any + still eligible);
    // fill the remaining slots with a date-seeded shuffle of the rest so we
    // never duplicate the leader.
    const winner = topNavId
      ? eligible.find((n) => n.id === topNavId) || null
      : null;
    const rest = eligible.filter((n) => !winner || n.id !== winner.id);
    const rng = makeRng(dateSeed());
    const shuffled = seededShuffle(rest, rng);
    const ordered = winner ? [winner, ...shuffled] : shuffled;
    return ordered.slice(0, 3).map((n) => ({
      ...n,
      _featured: !!winner && n.id === winner.id,
    }));
  }, [navItems, pageTiles, topNavId]);

  const tiles = useMemo(() => {
    if (!picks) return null;
    return picks.map((n) => {
      const pcKey = pageContentKeyFor(n.to);
      const slug = customPageSlugFor(n.to);
      let subtitle = "";
      let image_url = n.image_url || "";
      // For tiles sourced from `page_tiles`, use the tile's own subtitle.
      if (n._explore_tile_subtitle) {
        subtitle = n._explore_tile_subtitle;
      } else if (pcKey && pageContent[pcKey]) {
        // tile_blurb (if set) wins over the page intro so the tile copy
        // can be tighter than the page's own headline paragraph.
        const pc = pageContent[pcKey];
        subtitle = ((pc.tile_blurb && pc.tile_blurb.trim()) || (pc.intro || "")).trim();
      } else if (slug && customPageBySlug[slug]) {
        const cp = customPageBySlug[slug];
        subtitle = ((cp.tile_blurb && cp.tile_blurb.trim()) || (cp.intro || "")).trim();
        if (!image_url) {
          image_url = cp.cover_image || "";
        }
      }
      return {
        id: n.id,
        title: n.label,
        subtitle,
        to: n.to,
        target: n.target,
        external: isExternal(n.to) || n.target === "blank" || !!n._explore_force_external,
        image_url,
        tile_color: n.tile_color || DEFAULT_TILE_COLOR,
        featured: n._featured || false,
      };
    });
  }, [picks, pageContent, customPageBySlug]);

  if (tiles === null) {
    // Match the real grid's classes + per-tile minHeight exactly so this
    // loading state reserves the same space the final 3-tile grid takes
    // (was a single small text line — a huge CLS jump when tiles arrived).
    return (
      <div
        className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
        data-testid="home-rotating-tiles-loading"
        aria-busy="true"
        aria-label="Loading explore tiles"
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="animate-pulse rounded-md border border-black/10 bg-black/5"
            style={{ minHeight: "300px" }}
          />
        ))}
      </div>
    );
  }

  if (tiles.length === 0) {
    return (
      <div
        className="rounded-md border border-dashed border-black/15 p-8 text-center text-sm text-[var(--brand-body)]"
        data-testid="home-rotating-tiles-empty"
      >
        Add some menu items at <code>/admin/nav</code> and they'll start showing up here.
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
      data-testid="home-rotating-tiles-grid"
    >
      {tiles.map((t, i) => (
        <RotatingTile key={t.id} tile={t} index={i} />
      ))}
    </div>
  );
}


const RotatingTile = ({ tile, index }) => {
  const bgImage = tile.image_url ? resolveAsset(tile.image_url) : "";
  const handleClick = () => trackNavClick(tile.id);

  const content = (
    <>
      {bgImage ? (
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40 transition-opacity duration-500 group-hover:opacity-75"
          style={{ backgroundImage: `url(${bgImage})` }}
          aria-hidden
        />
      ) : null}
      <div
        className="absolute inset-0 transition-opacity duration-500 group-hover:opacity-75"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.65) 100%)",
        }}
        aria-hidden
      />
      {tile.featured ? (
        <div
          className="absolute right-4 top-4 z-10 rounded-sm bg-[#6CABDD] px-2 py-1 label-kicker text-[#0A0F17]"
          data-testid={`home-rotating-tile-${tile.id}-featured-badge`}
        >
          Top yesterday
        </div>
      ) : null}
      <div className="relative flex h-full flex-col justify-between p-8">
        <div className="flex items-start justify-between gap-4">
          <div className="flex h-11 w-11 items-center justify-center rounded-sm border border-white/30">
            {tile.external ? (
              <ExternalLink size={20} strokeWidth={2.2} />
            ) : (
              <Sparkles size={20} strokeWidth={2.2} />
            )}
          </div>
          <ArrowUpRight
            size={22}
            className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
          />
        </div>
        <div className="mt-auto">
          <span className="label-kicker text-[#6CABDD]">
            {tile.external ? "External" : "Explore"}
          </span>
          <h3 className="mt-3 font-display text-2xl font-black leading-[1.1] tracking-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
            {tile.title}
          </h3>
          {tile.subtitle ? (
            <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-white/90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
              {tile.subtitle}
            </p>
          ) : null}
          <div className="mt-6 flex items-center border-t border-white/10 pt-4">
            <span className="label-kicker text-white/80">
              {tile.external ? "Open link" : "Open"}
            </span>
          </div>
        </div>
      </div>
    </>
  );

  const tileColor = (!tile.tile_color || tile.tile_color === "#000000") ? "#002d75" : tile.tile_color;

  const sharedProps = {
    "data-testid": `home-rotating-tile-${tile.id}`,
    onClick: handleClick,
    className:
      "card-hover group relative flex h-full flex-col justify-between overflow-hidden rounded-md border border-white fade-in-up text-white",
    style: {
      animationDelay: `${index * 70}ms`,
      minHeight: "300px",
      backgroundColor: tileColor,
    },
  };

  if (tile.external || isExternal(tile.to)) {
    return (
      <a
        href={tile.to}
        target={tile.target === "blank" ? "_blank" : "_self"}
        rel="noopener noreferrer"
        {...sharedProps}
      >
        {content}
      </a>
    );
  }
  return (
    <Link to={tile.to} {...sharedProps}>
      {content}
    </Link>
  );
};
